import { ReactLocalization } from '@fluent/react';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { isFuture } from 'date-fns';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as ivimEvents from '../../app/assets/ivimEvents.json';
import { GetIVIMListPaylaod } from '../../lib/api';
import ivimsReducer from './ivimsReducers';
import { Ivim } from './ivimsTypes';
import {
  ivimFilterDateSelector,
  ivimFilterServiceCategoryCodeSelector,
  ivimFilterServiceSubCategoryCodeSelector,
  ivimFilterSourceSelector,
  ivimFilterStatusSelector,
  ivimFilterUpdatedFromSelector,
  ivimFilterUpdatedToSelector,
  ivimsSelectorForSelectedRoadSegment,
} from './selectors';

export const defaultIcon = 'BlueExclamation.png';

export const textLanguageISO639 = [
  { value: 'it', label: 'it' },
  { value: 'en', label: 'en' },
  { value: 'fr', label: 'fr' },
  { value: 'de', label: 'de' },
];

export const ivimStatuses = (ivim: Ivim) => {
  const disabled = ivim.disabled;
  const scheduled = isFuture(new Date(ivim.starts));
  const isIvimActive = disabled || scheduled ? false : isFuture(new Date(ivim.ends));
  return {
    isIvimActive,
    disabled,
    scheduled,
  };
};

export const mapPictogramText = (
  pictogramNature: number,
  pictogramSerialNumber: number,
  serviceCategoryCode: string,
  serviceSubCategoryCode: string,
) => {
  const matchedPictogram = ivimEvents.find(
    (i) =>
      i.pictogramCategoryCode === pictogramNature &&
      i.pictogramSerialNumber === pictogramSerialNumber &&
      i.serviceCategoryCode === serviceCategoryCode &&
      i.serviceSubCategoryCode === serviceSubCategoryCode,
  );
  if (matchedPictogram) {
    return matchedPictogram.description;
  }
  return '-';
};

export const useIvimsRequest = ({
  currentPage,
  itemsPerPage,
  getAllData,
}: {
  currentPage?: number;
  itemsPerPage?: number;
  getAllData?: boolean;
}) => {
  const dispatch = useDispatch();
  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);
  const ivimsStatus = useSelector(ivimFilterStatusSelector);
  const ivimsDate = useSelector(ivimFilterDateSelector);
  const ivimsUpdatedFrom = useSelector(ivimFilterUpdatedFromSelector);
  const ivimsUpdatedTo = useSelector(ivimFilterUpdatedToSelector);
  const ivimsServiceCategoryCode = useSelector(ivimFilterServiceCategoryCodeSelector);
  const ivimsServiceSubCategoryCode = useSelector(ivimFilterServiceSubCategoryCodeSelector);
  const ivimsSources = useSelector(ivimFilterSourceSelector);

  const dispatchIvimsRequest = (
    roadSegmentIds?: string[],
    overrideParams?: Partial<GetIVIMListPaylaod>,
  ) => {
    roadSegmentIds = roadSegmentIds?.length > 0 ? roadSegmentIds : selectedRoadSegmentIds;
    dispatch(
      ivimsReducer.actions.ivimsRequest({
        status: overrideParams?.status ?? ivimsStatus,
        roadSegmentIds: roadSegmentIds,
        date: overrideParams?.date ?? ivimsDate,
        updatedFrom: overrideParams?.updatedFrom ?? ivimsUpdatedFrom,
        updatedTo: overrideParams?.updatedTo ?? ivimsUpdatedTo,
        serviceCategoryCode: overrideParams?.serviceCategoryCode ?? ivimsServiceCategoryCode,
        serviceSubCategoryCode:
          overrideParams?.serviceSubCategoryCode ?? ivimsServiceSubCategoryCode,
        sources: overrideParams?.sources ?? ivimsSources,
        currentPage,
        itemsPerPage,
        getAllData,
      }),
    );
  };

  return dispatchIvimsRequest;
};

export const serviceProviderOption = {
  value: process.env.SERVICE_PROVIDER_ID || 'emeras',
  label: process.env.SERVICE_PROVIDER_ID || 'emeras',
};

export const getIvimTitle = (ivim: Ivim, l10n: ReactLocalization) => {
  if (ivim.metadata.datexii) {
    return `PMV / VMS ${ivim.metadata.datexii.description}`;
  }
  return `${mapServiceCategoryCode(ivim.serviceCategoryCode, l10n)} - ${mapServiceCategorySubCode(
    ivim.serviceSubCategoryCode,
    l10n,
  )}`;
};

export const mapServiceCategoryCode = (code: string, l10n: ReactLocalization) => {
  const serviceCategoryCode = [...new Set(ivimEvents.map((i) => i.serviceCategoryCode))];
  const matchedCode = serviceCategoryCode.find((c) => c === code);
  if (matchedCode) {
    return l10n.getString(`forms-ivim-ser-cat-cod-enum-${matchedCode}`);
  }
  return '-';
};

export const mapServiceCategorySubCode = (code: string, l10n: ReactLocalization) => {
  const serviceSubCategoryCode = [...new Set(ivimEvents.map((i) => i.serviceSubCategoryCode))];
  const matchedCode = serviceSubCategoryCode.find((c) => c === code);
  if (matchedCode) {
    return l10n.getString(`forms-ivim-ser-cat-sub-cod-enum-${matchedCode}`);
  }
  return '-';
};

export function useIvimsForRoadSegment(roadSegmentIds: string[]) {
  const memoizedSelector = useMemo(
    () => ivimsSelectorForSelectedRoadSegment(roadSegmentIds),
    [roadSegmentIds],
  );
  return useSelector(memoizedSelector);
}

export const mapPictogramCategoryId = (
  serviceCategoryCode: string,
  serviceSubCategoryCode: string,
) => {
  const categoryMap: Record<string, number> = {
    trafficSignPictogram: 1,
    publicFacilitiesPictogram: 2,
    ambientOrRoadConditionPictogram: 3,
  };

  const subCategoryMap: Record<string, Record<string, number>> = {
    trafficSignPictogram: {
      dangerWarning: 1,
      regulatory: 2,
      informative: 3,
    },
    publicFacilitiesPictogram: {
      publicFacilities: 1,
    },
    ambientOrRoadConditionPictogram: {
      ambientCondition: 1,
      roadCondition: 2,
    },
  };

  const categoryId = categoryMap[serviceCategoryCode];
  if (!categoryId) return null;

  const subCategoryId = subCategoryMap[serviceCategoryCode]?.[serviceSubCategoryCode];
  if (!subCategoryId) return null;

  return `${categoryId}${subCategoryId}`;
};
